import axios from "axios";
import {
    Thematic,
    ThematicDto,
    ThematicListOptions,
    UpsertThematicDTOSchema,
} from "../types/Thematic";
import qs from "qs";
import { SimpleFormSelectOption } from "../components/form/SimpleFormSelect";
import { getKeywordFromString, getWebsiteFromString } from "../utils/keywordsHelper";
import { toCamelCase } from "../utils/objectHelper";

export const ThematicTransport = {
    list: async (params?: object): Promise<Thematic[]> => {
        const { data } = await axios.get<Thematic[]>(
            `/thematics${params ? `?${qs.stringify(params)}` : ""}`
        );

        return data.map((el) => toCamelCase(el), { stripUnknown: true });
    },
    allOptions: async (params?: object): Promise<ThematicListOptions[]> => {
        const { data } = await axios.get<ThematicListOptions[]>(
            `/thematics/options${params ? `?${qs.stringify(params)}` : ""}`
        );

        return data;
    },

    toggleActive: async (platform: Thematic): Promise<void> => {
        const action = "toggleActive";
        await axios.patch<Thematic>(`/thematics/${platform.id}/actions/${action}`);
    },

    save: async (object: ThematicDto, id: number | undefined | null): Promise<Thematic> => {
        let keywords: object | null = null;
        let categories: object | null = null;
        let websites: object | null = null;

        const keywordLang = object.keywords ? Object.keys(object.keywords) : null;
        const websitesLang = object.websites ? Object.keys(object.websites) : null;
        const categoriesName = object.categories ? Object.keys(object.categories) : null;

        keywordLang?.forEach((lang) => {
            if (!keywords) {
                keywords = {};
            }

            if (!object.keywords) return;
            const keyword = object.keywords[lang as keyof typeof object.keywords];

            if (!keyword) return;

            keywords = {
                ...keywords,
                [lang]: getKeywordFromString(keyword),
            };
        });
        websitesLang?.forEach((lang) => {
            if (!websites) {
                websites = {};
            }

            if (!object.websites) return;
            const website = object.websites[lang as keyof typeof object.websites];

            if (!website) return;

            websites = {
                ...websites,
                [lang]: getWebsiteFromString(website),
            };
        });

        categoriesName?.forEach((name) => {
            if (!categories) {
                categories = {};
            }

            if (!object.categories) return;
            const category = object.categories[name as keyof typeof object.categories];

            if (!category) return;

            categories = {
                ...categories,
                [name]: getKeywordFromString(category),
            };
        });
        const thematic = {
            ...object,
            keywords,
            websites,
            categories,
        };

        const response = await axios.request<Thematic>({
            method: id ? "put" : "post",
            url: id ? `/thematics/${id}` : "/thematics",
            data: UpsertThematicDTOSchema.cast(thematic, { stripUnknown: true }),
        });

        return response.data;
    },

    findIsUsed: async (platform: Thematic): Promise<Array<{ total: string }>> => {
        const action = "findIsUsed";
        const { data } = await axios.get<Array<{ total: string }>>(
            `/thematics/${platform.id}/actions/${action}`
        );
        return data;
    },
    findByRdT1Options: async (moneySiteId: number): Promise<Array<SimpleFormSelectOption>> => {
        const { data } = await axios.get<Array<SimpleFormSelectOption>>(
            `/thematics/findByRdT1Options?moneySiteId=${moneySiteId}`
        );
        return data;
    },

    formTransformer: (object: Thematic): ThematicDto => {
        let keywords: object | null = null;
        let websites: object | null = null;
        let categories: { [x: string]: string } | null = null;

        const keywordLang = object.keywords ? Object.keys(object.keywords) : null;
        const websitesLang = object.websites ? Object.keys(object.websites) : null;
        const categoriesName = object.categories ? Object.keys(object.categories) : null;

        keywordLang?.forEach((lang) => {
            if (!keywords) {
                keywords = {};
            }

            if (!object.keywords) return;
            const keyword = object.keywords[lang as keyof typeof object.keywords];

            if (!keyword) return;

            keywords = {
                ...keywords,
                [lang.toUpperCase()]: keyword?.join("\n"),
            };
        });

        websitesLang?.forEach((lang) => {
            if (!websites) {
                websites = {};
            }

            if (!object.websites) return;
            const website = object.websites[lang as keyof typeof object.websites];

            if (!website) return;

            websites = {
                ...websites,
                [lang.toUpperCase()]: website?.join("\n"),
            };
        });

        categoriesName?.forEach((name) => {
            if (!categories) {
                categories = {};
            }

            if (!object.categories) return;
            const category = object.categories[name as keyof typeof object.categories];

            if (!category) return;

            categories = {
                ...categories,
                [name]: category?.join("\n"),
            };
        });

        if (!categories) {
            categories = {
                category1: "",
                category2: "",
                category3: "",
                category4: "",
                category5: "",
                category6: "",
                category7: "",
            };
        }

        return {
            ...object,
            categories,
            keywords,
            websites,
        };
    },
};
